// Die Tour — der Bildschirm, den der Fahrer den ganzen Dienstag anschaut.
//
// Fünf Phasen, immer nur eine sichtbar. Jeder Übergang ist eine bewusste
// Handlung, keine Nebenwirkung:
//   1. PACKLISTE STARTEN  "Ab jetzt wird geladen."
//   2. PACKEN             Packliste abhaken. Ohne vollständige Liste kein Start.
//   3. TOUR STARTEN       Übersicht, dann los.
//   4. FAHREN             Ein Stop nach dem anderen. Navigieren → übergeben → weiter.
//   5. ZURÜCK             Restware wieder einlagern.

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  useAnstehendeTouren,
  useTour,
  useToggleLoadItem,
  usePacklisteStarten,
  useConfirmPacklist,
  useStartTour,
  useCompleteStop,
  useFinishTour,
  useTourBauen,
  useTourAnlegen,
  useStopKorb,
  useTourUebernehmen,
  mapsUrl,
  nextStop,
  type TourStop,
} from "@/lib/tour";
import { useProducts, INTEREST_LABELS, type InterestTag } from "@/lib/db";
import { useKundenRechnungen, zahlungsstand } from "@/lib/buchhaltung";
import {
  useKundenpflege,
  useKundeBestaetigen,
  type KundenpflegeDaten,
} from "@/lib/kundenpflege";
import { useOffeneKasse, useKassenAbgabe, useKasseAbgeben } from "@/lib/cash";
import { useTeam } from "@/lib/qs";
import { useAuth } from "@/stores/authStore";
import { LogoLoader } from "@/components/LogoLoader";
import { ModulHeader } from "@/modules/home/ModulHeader";
import { SignaturePad } from "./SignaturePad";
import { Warteliste } from "./Warteliste";
import { TurnusSheet } from "@/modules/crm/TurnusSheet";
import {
  Button,
  Card,
  Chip,
  EmptyState,
  Field,
  Input,
  Segmented,
  Sheet,
  Stepper,
  cn,
  euro,
  qtyFmt,
} from "@/design-system/ui";

const heuteIso = () => new Date().toISOString().slice(0, 10);

/**
 * Welche Tour soll offen sein, wenn niemand etwas auswählt?
 *
 * Der Wagen wird am ABEND VORHER gepackt. Also: die erste Tour, die noch nicht
 * fertig gepackt ist — das ist meistens die von morgen. Ist alles gepackt, die
 * erste, die noch nicht gefahren wurde.
 */
function naechsteTour(touren: { tour_date: string; status: string; packlist_confirmed_at: string | null }[]) {
  const heute = heuteIso();
  const kuenftig = touren.filter((t) => t.tour_date >= heute);

  return (
    kuenftig.find((t) => !t.packlist_confirmed_at)?.tour_date ??
    kuenftig.find((t) => t.status !== "finished")?.tour_date ??
    // Nichts Künftiges offen? Dann eine Tour aus der Vergangenheit, die noch NICHT
    // abgeschlossen ist — daran wird noch gearbeitet. Die jüngste zuerst.
    // (Ohne das landet man auf "heute", wo es keine Tour gibt, während die eigene
    // laufende Tour mit offenen Stops danebensteht.)
    [...touren].reverse().find((t) => t.status !== "finished")?.tour_date ??
    // Sonst die letzte — sie kann noch eine Rückladeliste haben.
    touren[touren.length - 1]?.tour_date ??
    heute
  );
}

export default function TourPage() {
  const { data: touren = [], isLoading: laedtTouren } = useAnstehendeTouren();

  // Der gewählte Tag lebt in der URL (?datum=), nicht nur im State: So übersteht
  // er ein Neuladen, lässt sich verlinken, und das Backoffice kann gezielt einen
  // Tag öffnen. Streng validiert — alles, was nicht wie ein Datum aussieht, wird
  // ignoriert statt zu einer leeren Seite zu führen.
  const [params, setParams] = useSearchParams();
  const wunsch = params.get("datum");
  const gueltigerWunsch = wunsch && /^\d{4}-\d{2}-\d{2}$/.test(wunsch) ? wunsch : null;

  const date = gueltigerWunsch ?? (touren.length ? naechsteTour(touren) : heuteIso());

  // replace, damit der Zurück-Knopf nicht durch jeden angetippten Tag blättert.
  const waehlen = (d: string) => setParams(d ? { datum: d } : {}, { replace: true });

  const { data: tour, isLoading } = useTour(date);
  const meineNutzerId = useAuth((s) => s.user?.id ?? null);

  if (laedtTouren || isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LogoLoader size="lg" label="Tour wird geladen" />
      </div>
    );
  }

  // Die Tagesauswahl gehört ÜBER die Ansicht und ist IMMER da — auch bei nur
  // einer Tour: Der Fahrer soll jederzeit SEHEN, welchen Tag er fährt, und über
  // den Kalender jeden anderen Tag öffnen können, falls er mal einen anderen
  // fahren will. Sie sitzt über der Ansicht, nicht darin — sonst säße man in
  // einem leeren Tag fest, für den es (noch) keine Tour gibt.
  const auswahl = (
    <TagesWahl touren={touren} aktiv={date} onWaehlen={waehlen} tour={tour} date={date} />
  );

  if (!tour) {
    return (
      <div className="mx-auto flex h-full max-w-lg flex-col">
        <ModulHeader titel="Tour" untertitel={fmtDate(date)} />
        {auswahl}
        <KeineTour date={date} />
      </div>
    );
  }

  // ══ Der Tag hat vier Schritte, und jeder wird bewusst begonnen ══
  //
  //   Packliste starten  →  abhaken  →  Tour starten  →  fahren  →  zurückladen
  //
  // Der erste Schritt ist neu. Vorher landete man beim Öffnen eines Liefertages
  // sofort mitten in der Packliste — es gab kein "ab jetzt wird geladen".
  const packStarted = !!tour.packlist_started_at;
  const packDone = !!tour.packlist_confirmed_at;
  const running = tour.status === "active";
  const finished = tour.status === "finished" || !!tour.returnlist_confirmed_at;

  if (finished) return <ReturnView date={date} auswahl={auswahl} />;
  if (!packStarted) return <PackStartView date={date} auswahl={auswahl} />;
  if (!packDone) return <PackView date={date} auswahl={auswahl} />;
  if (!running) return <StartView date={date} auswahl={auswahl} />;

  // Eine laufende Tour gehört dem Fahrer, der sie fährt. Wer NICHT dieser Fahrer
  // ist — Büro, Disposition, ein zusehender Admin — bekommt die Tour zu sehen
  // (wo steht er, wie weit ist er), aber keinen einzigen Bearbeiten-Knopf. Der
  // Server weist Fremdzugriffe ohnehin ab; der Monitor sorgt dafür, dass gar
  // nicht erst ein Knopf dasteht, der ins Leere führte.
  if (tour.driver_id && tour.driver_id !== meineNutzerId) {
    return <MonitorView date={date} auswahl={auswahl} />;
  }
  return <DriveView date={date} auswahl={auswahl} />;
}

/**
 * Kein Tour für den gewählten Tag — mit einem Knopf, der sie baut.
 *
 * Der Knopf ist die Reissleine gegen einen ausgefallenen Nachtlauf: Ohne ihn
 * stünden die Bestellungen in der Datenbank, die App sagte "keine Tour", und
 * niemand könnte etwas tun. Er baut nur dann etwas, wenn für den Tag auch
 * wirklich Bestellungen vorliegen — sonst sagt der Server das klar.
 */
function KeineTour({ date }: { date: string }) {
  const rolle = useAuth((s) => s.role);
  const bauen = useTourBauen(date);
  const anlegen = useTourAnlegen(date);
  const [fehler, setFehler] = useState<string | null>(null);
  const darf = ["admin", "buero", "dispatch"].includes(rolle);

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
      <p className="text-[15px] font-medium text-ink-300">Für diesen Tag gibt es keine Tour</p>
      <p className="max-w-xs text-sm text-ink-500">
        {darf
          ? "Liegen für den Tag Bestellungen vor, lässt sie sich bauen. Sonst eine leere Tour anlegen und die Kunden von Hand eintragen."
          : "An diesem Tag wird nicht geliefert."}
      </p>

      {fehler && (
        <p className="max-w-xs rounded-lg border border-danger-bg/30 bg-danger-bg/10 px-3 py-2 text-sm text-danger-fg">
          {fehler}
        </p>
      )}

      {darf && (
        <div className="mt-1 flex flex-col items-center gap-2">
          <Button
            variant="primary"
            loading={bauen.isPending}
            onClick={() => {
              setFehler(null);
              bauen.mutate(undefined, { onError: (e) => setFehler((e as Error).message) });
            }}
          >
            Tour jetzt bauen
          </Button>
          {/* Für Tage, an denen keine Abos fällig sind: Die Route wird bewusst
              NICHT optimiert — bei einer Handvoll Kunden weiß der Fahrer die
              bessere Reihenfolge selbst und kann sie in der Warteliste ziehen. */}
          <Button
            variant="ghost"
            size="sm"
            loading={anlegen.isPending}
            onClick={() => {
              setFehler(null);
              anlegen.mutate(undefined, { onError: (e) => setFehler((e as Error).message) });
            }}
          >
            Leere Tour anlegen
          </Button>
        </div>
      )}
    </div>
  );
}

type TagInfo = { tour_date: string; status: string; packlist_confirmed_at: string | null };

/**
 * Die Liefertage als Leiste — plus ein Kalender, um JEDEN Tag zu öffnen.
 *
 * "Heute" und "Morgen" statt Datum: Der Fahrer packt abends für morgen, und
 * "Mi 15.07." zwingt ihn, im Kopf nachzurechnen, ob das jetzt morgen ist.
 *
 * Der Kalender-Knopf rechts ist der Fall "ich will heute mal die Tour von einem
 * anderen Tag fahren": Er öffnet den nativen Datumswähler und lässt jeden Tag
 * aufrufen — auch einen, für den keine anstehende Tour im Fenster liegt.
 */
function TagesWahl({
  touren,
  aktiv,
  onWaehlen,
  tour,
  date,
}: {
  touren: TagInfo[];
  aktiv: string;
  onWaehlen: (d: string) => void;
  /** Die geladene Tour des aktiven Tags — für die Kunden-Verwaltung. */
  tour?: { id: string; status: string; stops: TourStop[] } | null;
  date: string;
}) {
  const [kundenOffen, setKundenOffen] = useState(false);
  const heute = heuteIso();
  const morgen = new Date();
  morgen.setDate(morgen.getDate() + 1);
  const morgenIso = morgen.toISOString().slice(0, 10);

  // Der aktive Tag MUSS als Chip auftauchen — auch wenn er per Kalender außerhalb
  // des 7-Tage-Fensters gewählt wurde. Sonst wählt man einen fernen Tag und die
  // Leiste zeigt ihn nicht, man weiß nicht mehr, wo man ist.
  const tage: TagInfo[] = touren.some((t) => t.tour_date === aktiv)
    ? touren
    : [...touren, { tour_date: aktiv, status: "?", packlist_confirmed_at: null }].sort((a, b) =>
        a.tour_date.localeCompare(b.tour_date)
      );

  const label = (iso: string) => {
    if (iso === heute) return "Heute";
    if (iso === morgenIso) return "Morgen";
    return new Date(iso + "T00:00:00").toLocaleDateString("de-DE", {
      weekday: "short",
      day: "2-digit",
      month: "2-digit",
    });
  };

  return (
    <div className="flex shrink-0 items-center gap-2 border-b border-ink-700/70 px-4 py-2.5">
      <div className="flex flex-1 gap-1.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {tage.map((t) => {
          const istAktiv = t.tour_date === aktiv;
          const gepackt = !!t.packlist_confirmed_at;
          const gefahren = t.status === "finished";

          return (
            <button
              key={t.tour_date}
              onClick={() => onWaehlen(t.tour_date)}
              aria-pressed={istAktiv}
              className={cn(
                "flex h-9 shrink-0 items-center gap-1.5 rounded-full border px-3.5 text-sm font-medium",
                "transition-colors duration-fast",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gruen-500/60",
                istAktiv
                  ? "border-gruen-500/50 bg-gruen-500/15 text-gruen-400"
                  : "border-ink-700 bg-ink-800/60 text-ink-300 hover:border-ink-600"
              )}
            >
              {label(t.tour_date)}
              {gefahren ? (
                <span className="text-xs text-ink-500" title="gefahren">
                  ✓
                </span>
              ) : gepackt ? (
                <span className="h-1.5 w-1.5 rounded-full bg-gruen-400" title="gepackt" />
              ) : null}
            </button>
          );
        })}
      </div>

      {/* Die Warteliste der Tour: was ist erledigt, was kommt noch, Reihenfolge
          ändern, Kunden dazu oder weg. Nur wenn für den Tag eine Tour existiert.
          Das Abzeichen zeigt "erledigt/gesamt" — die häufigste Frage unterwegs
          ist damit schon beantwortet, ohne dass man es öffnen muss. */}
      {tour && (
        <button
          onClick={() => setKundenOffen(true)}
          aria-label="Warteliste der Tour"
          className="flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-ink-700 bg-ink-800/60 px-3 text-sm font-medium text-ink-300 transition-colors duration-fast hover:border-ink-600 hover:text-ink-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gruen-500/60"
        >
          <IconPersonen />
          <span className="tabular-nums">
            {tour.stops.filter((s) => s.status !== "pending" && s.status !== "arrived").length}
            <span className="text-ink-500">/{tour.stops.length}</span>
          </span>
        </button>
      )}

      {/* Kalender: jeden beliebigen Tag öffnen. Der transparente date-Input liegt
          über dem Icon — ein Tipp öffnet den nativen Datumswähler, auf jeder
          Plattform gleich. Gleiche Höhe (h-9) wie die Chips, damit die Leiste
          nicht ausfranst. */}
      <div className="relative h-9 w-9 shrink-0">
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-full border border-ink-700 bg-ink-800/60 text-ink-300">
          <IconKalender />
        </div>
        <input
          type="date"
          value={aktiv}
          onChange={(e) => e.target.value && onWaehlen(e.target.value)}
          aria-label="Anderen Tag wählen"
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
        />
      </div>

      {tour && (
        <Warteliste
          open={kundenOffen}
          onClose={() => setKundenOffen(false)}
          tour={tour}
          date={date}
        />
      )}
    </div>
  );
}

function IconPersonen() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M16 20v-1.5a3.5 3.5 0 0 0-3.5-3.5h-5A3.5 3.5 0 0 0 4 18.5V20" />
      <circle cx="10" cy="8" r="3.2" />
      <path d="M20 20v-1.5a3.5 3.5 0 0 0-2.6-3.4M15.5 4.8a3.2 3.2 0 0 1 0 6.1" />
    </svg>
  );
}

function IconKalender() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3.5" y="5" width="17" height="16" rx="2" />
      <path d="M3.5 9.5h17M8 3.5v3M16 3.5v3" />
    </svg>
  );
}

// ═══════════════════════════════════════════════════ 1. PACKLISTE STARTEN

/**
 * Das Tor vor der Packliste: "Ab jetzt wird geladen."
 *
 * Vorher fiel man beim Öffnen eines Liefertages sofort in die Packliste. Der
 * Bildschirm zeigt jetzt erst, WAS ansteht (wie viele Kunden, wie viele
 * Positionen, wie weit), und lässt den Start eine bewusste Handlung sein.
 */
function PackStartView({ date, auswahl }: { date: string; auswahl?: React.ReactNode }) {
  const { data: tour } = useTour(date);
  const starten = usePacklisteStarten(date);
  const [fehler, setFehler] = useState<string | null>(null);

  const positionen = (tour?.load_items ?? []).filter((i) => i.kind === "pack").length;
  const km = tour?.planned_distance_m ? Math.round(tour.planned_distance_m / 100) / 10 : null;

  async function los() {
    if (!tour) return;
    setFehler(null);
    try {
      await starten.mutateAsync(tour.id);
    } catch (e) {
      // Ein Knopf, der nichts tut und nichts sagt, ist schlimmer als ein Fehler:
      // Der Fahrer tippt dreimal und weiß nie, ob es an ihm oder am Netz lag.
      const m = (e as Error).message ?? "";
      setFehler(
        /failed to fetch|load failed|networkerror/i.test(m)
          ? "Kein Netz — die Packliste konnte nicht gestartet werden. Bitte noch einmal tippen."
          : m || "Die Packliste konnte nicht gestartet werden. Bitte noch einmal tippen."
      );
    }
  }

  return (
    <div className="mx-auto flex h-full max-w-lg flex-col">
      <Header title="Beladen" subtitle={fmtDate(date)} />
      {auswahl}

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4">
        <div className="grid grid-cols-3 gap-3">
          <Stat label="Kunden" value={String(tour?.stops.length ?? 0)} />
          <Stat label="Positionen" value={String(positionen)} />
          <Stat label="Strecke" value={km ? `${km} km` : "—"} />
        </div>

        <Card className="p-4">
          <p className="text-[15px] text-ink-200">
            Sobald du startest, siehst du die Packliste — alles, was für{" "}
            {fmtDate(date)} ins Auto muss. Erst wenn sie vollständig abgehakt ist,
            lässt sich die Tour starten.
          </p>
        </Card>

        {fehler && (
          <Card className="border-danger-bg/40 bg-danger-bg/10 p-4">
            <p className="text-[15px] text-danger-fg">{fehler}</p>
          </Card>
        )}
      </div>

      <Footer>
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          loading={starten.isPending}
          disabled={!tour}
          onClick={los}
        >
          Packliste für {fmtDate(date)} starten
        </Button>
      </Footer>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════ 2. PACKEN

function PackView({ date, auswahl }: { date: string; auswahl?: React.ReactNode }) {
  const { data: tour } = useTour(date);
  const { data: products = [] } = useProducts();
  const toggle = useToggleLoadItem(date);
  const confirm = useConfirmPacklist(date);
  // Scheitert das Bestätigen (Funkloch, Konflikt), MUSS der Fahrer das sehen.
  // Vorher passierte schlicht nichts, und er tippt ratlos weiter.
  const [fehler, setFehler] = useState<string | null>(null);

  /** Welche Position ist gerade aufgeklappt (zeigt, welcher Kunde was bekommt)? */
  const [offen, setOffen] = useState<string | null>(null);

  const items = useMemo(
    () => (tour?.load_items ?? []).filter((i) => i.kind === "pack"),
    [tour]
  );
  const done = items.filter((i) => i.checked_at).length;
  // Manchmal gibt es nichts zu packen: die Mengen stehen noch nicht fest und
  // werden erst beim Kunden eingetragen. Dann ist die Tour trotzdem startbar —
  // sonst hängt der Fahrer an einem grauen Knopf, weil die Liste nie „voll" wird.
  const nichtsZuPacken = items.length === 0;
  const all = nichtsZuPacken || done === items.length;

  const nameOf = (id: string) => products.find((p) => p.id === id);

  /**
   * Wer bekommt welchen Sack?
   *
   * Beim Beladen reicht die Gesamtzahl. Aber wenn am Ende der Tour ein Sack übrig
   * ist (oder einer fehlt), will man nachsehen können, ohne 221 Stops
   * durchzublättern.
   */
  const kundenJeProdukt = useMemo(() => {
    const m = new Map<string, { name: string; anzahl: number }[]>();
    for (const s of tour?.stops ?? []) {
      const o = Array.isArray(s.order) ? s.order[0] : s.order;
      if (!o) continue;
      const kunde = (Array.isArray(o.customer) ? o.customer[0] : o.customer)?.display_name ?? "?";
      for (const i of o.items ?? []) {
        const liste = m.get(i.product_id) ?? [];
        liste.push({ name: kunde, anzahl: Number(i.qty) });
        m.set(i.product_id, liste);
      }
    }
    return m;
  }, [tour]);

  // Kartoffeln werden in Säcken geladen, nicht in Kilo — aber wie viel das am Ende
  // wiegt, entscheidet, ob es in den Wagen passt.
  const kartoffelKg = items.reduce((s, it) => {
    const p = nameOf(it.product_id);
    return s + (p?.package_kg ? Number(it.qty_total) * Number(p.package_kg) : 0);
  }, 0);

  return (
    <div className="mx-auto flex h-full max-w-lg flex-col">
      <Header
        title="Packliste"
        subtitle={`${fmtDate(date)} · ${tour?.stops.length ?? 0} Kunden`}
      />
      {auswahl}

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        <p className="mb-4 text-sm text-ink-400">
          {nichtsZuPacken
            ? "Für diese Tour ist nichts vorbereitet. Du kannst direkt starten und beim Kunden eintragen, was er bekommt."
            : "Alles ins Auto laden und abhaken. Die Tour startet erst, wenn die Liste vollständig ist."}
        </p>

        <ul className="space-y-2">
          {items.map((it) => {
            const p = nameOf(it.product_id);
            const checked = !!it.checked_at;
            const istSack = !!p?.package_kg;
            const kunden = kundenJeProdukt.get(it.product_id) ?? [];
            const aufgeklappt = offen === it.id;

            return (
              <li key={it.id}>
                <button
                  onClick={() => toggle.mutate({ id: it.id, checked: !checked })}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-t-xl border px-4 py-4 text-left transition-colors duration-fast",
                    aufgeklappt ? "rounded-b-none" : "rounded-b-xl",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gruen-500/60",
                    checked
                      ? "border-gruen-500/30 bg-gruen-500/10"
                      : "border-ink-700 bg-ink-800/60 hover:border-ink-600"
                  )}
                >
                  <span
                    className={cn(
                      "flex h-7 w-7 shrink-0 items-center justify-center rounded-md border-2 text-sm font-bold",
                      checked
                        ? "border-gruen-500 bg-gruen-500 text-ink-950"
                        : "border-ink-600"
                    )}
                  >
                    {checked && "✓"}
                  </span>

                  <span className="min-w-0 flex-1">
                    <span
                      className={cn(
                        "block text-[15px]",
                        checked ? "text-ink-400 line-through" : "text-ink-100"
                      )}
                    >
                      {p?.name ?? "?"}
                    </span>
                    {istSack && (
                      <span className="mt-0.5 block text-xs tabular-nums text-ink-500">
                        {qtyFmt(Number(it.qty_total) * Number(p!.package_kg))} kg gesamt
                      </span>
                    )}
                  </span>

                  <span className="shrink-0 text-lg font-semibold tabular-nums text-ink-50">
                    {qtyFmt(it.qty_total)}
                    <span className="ml-1 text-sm font-normal text-ink-400">
                      {istSack ? "Sack" : p?.unit === "kg" ? "kg" : "St."}
                    </span>
                  </span>
                </button>

                {/* Wer bekommt das? — bewusst ein eigener, schmaler Streifen unter
                    der Zeile. Er darf das Abhaken nicht behindern: Der Daumen
                    trifft die große Fläche, nicht aus Versehen die Kundenliste. */}
                {kunden.length > 0 && (
                  <div
                    className={cn(
                      "-mt-px overflow-hidden rounded-b-xl border border-t-0",
                      checked ? "border-gruen-500/30" : "border-ink-700"
                    )}
                  >
                    <button
                      onClick={() => setOffen(aufgeklappt ? null : it.id)}
                      className="flex w-full items-center justify-between px-4 py-2 text-xs text-ink-400 transition-colors duration-fast hover:bg-ink-800/60"
                    >
                      <span>
                        {aufgeklappt ? "Kunden ausblenden" : `für ${kunden.length} Kunden`}
                      </span>
                      <span className={cn("transition-transform", aufgeklappt && "rotate-180")}>
                        ▾
                      </span>
                    </button>

                    {aufgeklappt && (
                      <ul className="max-h-64 space-y-0.5 overflow-y-auto border-t border-ink-800 bg-ink-900/60 px-4 py-2">
                        {kunden.map((k, i) => (
                          <li
                            key={`${k.name}-${i}`}
                            className="flex items-baseline justify-between gap-3 text-sm"
                          >
                            <span className="truncate text-ink-300">{k.name}</span>
                            <span className="shrink-0 tabular-nums text-ink-400">
                              {qtyFmt(k.anzahl)}
                              {istSack ? "× Sack" : "×"}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        {kartoffelKg > 0 && (
          <p className="mt-4 rounded-lg border border-ink-700 bg-ink-900 px-4 py-2.5 text-sm tabular-nums text-ink-400">
            Kartoffeln insgesamt: {qtyFmt(kartoffelKg)} kg
          </p>
        )}
      </div>

      <Footer>
        {!nichtsZuPacken && (
          <>
            <div className="mb-3 flex items-center justify-between text-sm">
              <span className="text-ink-400">Geladen</span>
              <span className="tabular-nums text-ink-100">
                {done} / {items.length}
              </span>
            </div>
            <div className="mb-3 h-1 overflow-hidden rounded-full bg-ink-800">
              <div
                className="h-full bg-gruen-500 transition-all"
                style={{ width: `${items.length ? (done / items.length) * 100 : 0}%` }}
              />
            </div>
          </>
        )}
        {fehler && (
          <p className="mb-2 rounded-lg border border-danger-bg/30 bg-danger-bg/10 px-3 py-2 text-sm text-danger-fg">
            {fehler}
          </p>
        )}
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          disabled={!all}
          loading={confirm.isPending}
          onClick={() =>
            tour &&
            confirm.mutate(tour.id, {
              onError: (e) => setFehler(fahrerFehler(e, "Bestätigen fehlgeschlagen.")),
            })
          }
        >
          {nichtsZuPacken
            ? "Ohne Packliste starten"
            : all
              ? "Packliste bestätigen"
              : `Noch ${items.length - done} Positionen`}
        </Button>
      </Footer>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════ 3. TOUR STARTEN

function StartView({ date, auswahl }: { date: string; auswahl?: React.ReactNode }) {
  const { data: tour } = useTour(date);
  const start = useStartTour(date);

  const km = tour?.planned_distance_m ? Math.round(tour.planned_distance_m / 100) / 10 : null;
  const anchors = (tour?.stops ?? []).filter((s) => s.order.customer.preferred_time);

  return (
    <div className="mx-auto flex h-full max-w-lg flex-col">
      <Header title="Tour bereit" subtitle={fmtDate(date)} />
      {auswahl}

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4">
        <div className="grid grid-cols-2 gap-3">
          <Stat label="Kunden" value={String(tour?.stops.length ?? 0)} />
          <Stat label="Strecke" value={km ? `${km} km` : "—"} />
        </div>

        {anchors.length > 0 && (
          <Card className="p-4">
            <h3 className="mb-2.5 text-xs font-medium uppercase tracking-wider text-ink-400">
              Feste Uhrzeiten
            </h3>
            <ul className="space-y-1.5">
              {anchors.map((s) => (
                <li key={s.id} className="flex items-baseline justify-between gap-3 text-[15px]">
                  <span className="min-w-0 truncate text-ink-200">
                    {s.order.customer.display_name}
                  </span>
                  <span className="shrink-0 tabular-nums text-gruen-400">
                    {s.order.customer.preferred_time?.slice(0, 5)}
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-2.5 text-sm text-ink-500">
              Die Route ist um diese Zeiten herum gebaut (±15 min).
            </p>
          </Card>
        )}
      </div>

      <Footer>
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          loading={start.isPending}
          onClick={() => tour && start.mutate(tour.id)}
        >
          Tour für {fmtDate(date)} starten
        </Button>
      </Footer>
    </div>
  );
}

// ════════════════════════════════════════════════════════════ 4. FAHREN

/** Auch beim Fahren die volle Tagesauswahl (mit Kalender): Ein Tagwechsel zeigt
 *  nur eine ANDERE Tour an — die laufende bleibt in der Datenbank unangetastet.
 *  Er kann also jederzeit nachsehen oder mal einen anderen Tag fahren. */
/**
 * Die laufende Tour aus Sicht von Büro, Disposition oder einem zusehenden Admin.
 *
 * Reines Zusehen: wo steht der Fahrer, wie weit ist er, was fehlt noch. KEIN
 * Bearbeiten-Knopf — die Tour gehört dem Fahrer. Einzige Ausnahme ganz unten:
 * Ein Admin kann sie im Notfall übernehmen (Fahrer ausgefallen). Danach ist er
 * der Fahrer und bekommt die normale Fahransicht.
 */
function MonitorView({ date, auswahl }: { date: string; auswahl?: React.ReactNode }) {
  const { data: tour } = useTour(date);
  const istAdmin = useAuth((s) => s.role) === "admin";
  const uebernehmen = useTourUebernehmen(date);
  const [fehler, setFehler] = useState<string | null>(null);
  const [fragt, setFragt] = useState(false);

  const stops = (tour?.stops ?? []).slice().sort((a, b) => a.seq - b.seq);
  const done = stops.filter((s) => s.status !== "pending" && s.status !== "arrived").length;
  const cash = stops.reduce((sum, s) => sum + (s.cash_collected_cents ?? 0), 0);
  const aktuell = nextStop(tour);
  const anteil = stops.length ? (done / stops.length) * 100 : 0;

  return (
    <div className="mx-auto flex h-full max-w-lg flex-col">
      <Header title="Tour läuft" subtitle={`${done} von ${stops.length} · ${euro(cash)} bar`} />
      {auswahl}

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        {/* Klar sagen: hier wird nur zugesehen. */}
        <div className="mb-4 flex items-center gap-3 rounded-xl border border-gruen-500/40 bg-gruen-500/10 px-4 py-3">
          <span className="relative flex h-2.5 w-2.5 shrink-0">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gruen-500/70" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-gruen-500" />
          </span>
          <p className="text-[15px] font-medium text-gruen-400">
            Die Tour wird gerade gefahren — nur der Fahrer kann sie bearbeiten.
          </p>
        </div>

        {/* Fortschritt */}
        <div className="mb-5">
          <div className="mb-1.5 flex items-baseline justify-between text-sm">
            <span className="text-ink-400">Fortschritt</span>
            <span className="tabular-nums text-ink-300">
              {done} / {stops.length}
            </span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-ink-800">
            <div className="h-full rounded-full bg-gruen-500 transition-[width]" style={{ width: `${anteil}%` }} />
          </div>
        </div>

        {/* Wo steht er gerade */}
        {aktuell && (
          <Card className="mb-5 p-4">
            <h3 className="mb-1 text-xs font-medium uppercase tracking-wider text-ink-400">Gerade dran</h3>
            <p className="truncate text-lg font-semibold text-ink-50">
              {aktuell.seq}. {aktuell.order.customer.display_name}
            </p>
            <p className="truncate text-[15px] text-ink-300">{aktuell.order.address.street}</p>
          </Card>
        )}

        {/* Vollständige Liste, nur zum Lesen */}
        <SectionLabel>Alle Stopps</SectionLabel>
        <ul className="mt-1 space-y-1">
          {stops.map((s) => {
            const erledigt = s.status !== "pending" && s.status !== "arrived";
            const dran = s.id === aktuell?.id;
            return (
              <li
                key={s.id}
                className={cn(
                  "flex items-center gap-3 rounded-lg border px-3 py-2",
                  dran
                    ? "border-gruen-500/40 bg-gruen-500/5"
                    : "border-ink-700/70 bg-ink-900"
                )}
              >
                <span className="w-6 shrink-0 text-right text-sm tabular-nums text-ink-500">{s.seq}</span>
                <span className="min-w-0 flex-1">
                  <span
                    className={cn(
                      "block truncate text-[15px]",
                      erledigt ? "text-ink-500 line-through" : "text-ink-100"
                    )}
                  >
                    {s.order.customer.display_name}
                  </span>
                  <span className="block truncate text-sm text-ink-500">{s.order.address.street}</span>
                </span>
                <span className="shrink-0 text-sm">
                  {s.status === "delivered" && <span className="text-gruen-400">geliefert</span>}
                  {s.status === "absent" && <span className="text-warn-fg">nicht da</span>}
                  {s.status === "skipped" && <span className="text-ink-500">übersprungen</span>}
                  {dran && <span className="text-gruen-400">→ jetzt</span>}
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Notfall-Übernahme — nur für Admins. */}
      {istAdmin && (
        <Footer>
          {fehler && (
            <p className="mb-2 rounded-lg border border-danger-bg/30 bg-danger-bg/10 px-3 py-2 text-sm text-danger-fg">
              {fehler}
            </p>
          )}
          {fragt ? (
            <div className="space-y-2">
              <p className="text-sm text-ink-300">
                Die Tour wird auf dich umgeschrieben — der bisherige Fahrer verliert dann den
                Zugriff. Nur im Notfall (Fahrer ausgefallen).
              </p>
              <div className="flex gap-2">
                <Button variant="ghost" size="lg" className="flex-1" onClick={() => setFragt(false)}>
                  Abbrechen
                </Button>
                <Button
                  variant="primary"
                  size="lg"
                  className="flex-1"
                  loading={uebernehmen.isPending}
                  onClick={() =>
                    tour &&
                    uebernehmen.mutate(tour.id, {
                      onError: (e) => setFehler(fahrerFehler(e, "Übernahme fehlgeschlagen.")),
                    })
                  }
                >
                  Jetzt übernehmen
                </Button>
              </div>
            </div>
          ) : (
            <Button variant="subtle" size="lg" className="w-full" onClick={() => setFragt(true)}>
              Tour übernehmen (Notfall)
            </Button>
          )}
        </Footer>
      )}
    </div>
  );
}

function DriveView({ date, auswahl }: { date: string; auswahl?: React.ReactNode }) {
  const { data: tour } = useTour(date);
  const finish = useFinishTour(date);
  const [endeFehler, setEndeFehler] = useState<string | null>(null);
  const [sheetStop, setSheetStop] = useState<TourStop | null>(null);

  const stop = nextStop(tour);
  const stops = tour?.stops ?? [];
  const done = stops.filter((s) => s.status !== "pending" && s.status !== "arrived").length;
  const cash = stops.reduce((sum, s) => sum + (s.cash_collected_cents ?? 0), 0);

  if (!stop) {
    return (
      <div className="mx-auto flex h-full max-w-lg flex-col">
        <Header title="Alle Stopps erledigt" subtitle={fmtDate(date)} />
        {auswahl}
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4">
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Ausgeliefert" value={`${stops.filter((s) => s.status === "delivered").length}`} />
            <Stat label="Bar kassiert" value={euro(cash)} />
          </div>
          {stops.some((s) => s.status === "absent") && (
            <Card className="p-4">
              <h3 className="mb-2 text-xs font-medium uppercase tracking-wider text-ink-400">
                Nicht angetroffen
              </h3>
              <ul className="space-y-1 text-[15px] text-ink-300">
                {stops
                  .filter((s) => s.status === "absent")
                  .map((s) => (
                    <li key={s.id} className="truncate">
                      {s.order.customer.display_name}
                    </li>
                  ))}
              </ul>
              <p className="mt-2 text-sm text-ink-500">
                Diese Ware kommt auf die Rückladeliste.
              </p>
            </Card>
          )}
        </div>
        <Footer>
          {/* Ein gescheitertes Tourende ist kein Schönheitsfehler: Die
              Rückladeliste entsteht erst dabei. Bleibt es stumm, fährt der
              Fahrer mit voller Ware nach Hause und niemand weiss davon. */}
          {endeFehler && (
            <p className="mb-2 rounded-lg border border-danger-bg/30 bg-danger-bg/10 px-3 py-2 text-sm text-danger-fg">
              {endeFehler}
            </p>
          )}
          <Button
            variant="primary"
            size="lg"
            className="w-full"
            loading={finish.isPending}
            onClick={() =>
              tour &&
              finish.mutate(tour.id, {
                onError: (e) => setEndeFehler(fahrerFehler(e, "Tour beenden fehlgeschlagen.")),
              })
            }
          >
            Tour beenden
          </Button>
        </Footer>
      </div>
    );
  }

  const c = stop.order.customer;
  const a = stop.order.address;
  const hint = a.delivery_notes ?? c.notes;

  return (
    <div className="mx-auto flex h-full max-w-lg flex-col">
      <Header
        title={`Stop ${stop.seq} von ${stops.length}`}
        subtitle={`${done} erledigt · ${euro(cash)} bar`}
      />
      {auswahl}

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        {/* Wer und wo */}
        <div className="mb-4">
          <div className="flex items-baseline gap-2">
            <h2 className="min-w-0 truncate text-2xl font-semibold text-ink-50">
              {c.display_name}
            </h2>
            {c.preferred_time && (
              <span className="shrink-0 rounded bg-gruen-500/15 px-2 py-0.5 text-sm font-medium tabular-nums text-gruen-400">
                {c.preferred_time.slice(0, 5)}
              </span>
            )}
          </div>
          <p className="mt-1 text-base text-ink-300">{a.street}</p>
        </div>

        {/* Lieferhinweis — das Wichtigste am Stop */}
        {hint && (
          <div className="mb-4 rounded-xl border border-warn-bg/30 bg-warn-bg/10 px-4 py-3">
            <p className="text-[15px] font-medium text-warn-fg">{hint}</p>
          </div>
        )}

        {/* Zahlungsstand: Hat der Kunde beim letzten Mal bezahlt? Genau das,
            was man an der Haustür wissen muss — bevor man klingelt. */}
        <Zahlungshinweis customerId={c.id} />

        {/* Korb */}
        <Card className="mb-4 p-4">
          <h3 className="mb-2.5 text-xs font-medium uppercase tracking-wider text-ink-400">
            Zu übergeben
          </h3>
          <ItemLines stop={stop} />
          <div className="mt-3 flex items-baseline justify-between border-t border-ink-700/70 pt-3">
            <span className="text-sm text-ink-400">Summe</span>
            <span className="text-xl font-semibold tabular-nums text-ink-50">
              {euro(stop.order.total_cents)}
            </span>
          </div>
        </Card>

        {/* Upsell */}
        {c.interest_tags?.length > 0 && (
          <div className="mb-4">
            <p className="mb-2 text-sm text-ink-500">Nimmt gern mit:</p>
            <div className="flex flex-wrap gap-2">
              {c.interest_tags.map((t) => (
                <Chip key={t} active>
                  {INTEREST_LABELS[t as InterestTag] ?? t}
                </Chip>
              ))}
            </div>
          </div>
        )}
      </div>

      <Footer>
        <div className="flex gap-2">
          {/* EIN Ziel an Google Maps — läuft in CarPlay / Android Auto.
              Nutzt bewusst dieselben Button-Klassen wie "Übergeben" daneben,
              statt handgestylt zu sein: sonst stehen zwei Knöpfe mit
              unterschiedlicher Schriftgröße nebeneinander. */}
          <a
            href={mapsUrl(stop)}
            target="_blank"
            rel="noreferrer"
            className={cn(
              "inline-flex h-14 flex-1 items-center justify-center gap-2.5 rounded-lg px-6 text-base font-medium",
              "border border-ink-700 bg-ink-800 text-ink-100 transition-colors duration-fast hover:bg-ink-700",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gruen-500/60"
            )}
          >
            Navigieren
          </a>
          <Button
            variant="primary"
            size="lg"
            className="flex-1"
            onClick={() => setSheetStop(stop)}
          >
            Übergeben
          </Button>
        </div>
      </Footer>

      {sheetStop && (
        <HandoverSheet
          date={date}
          stop={sheetStop}
          onClose={() => setSheetStop(null)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────── Übergabe-Sheet

function HandoverSheet({
  date,
  stop,
  onClose,
}: {
  date: string;
  stop: TourStop;
  onClose: () => void;
}) {
  const complete = useCompleteStop(date);
  const [method, setMethod] = useState<"cash" | "invoice">("cash");
  const [signature, setSignature] = useState<string | null>(null);
  const [absentMode, setAbsentMode] = useState(false);
  const [reason, setReason] = useState("nicht angetroffen");
  const [error, setError] = useState<string | null>(null);
  const [pflegeOffenSheet, setPflegeOffenSheet] = useState(false);
  const [korbOffen, setKorbOffen] = useState(false);
  const [turnusOffen, setTurnusOffen] = useState(false);

  // ══ Warum hier NICHT der übergebene Stop benutzt wird ══
  //
  // Das Sheet bekommt seinen Stop beim Öffnen als Kopie in den State gelegt
  // (`setSheetStop(stop)`). Diese Kopie altert: Bestätigt der Fahrer die
  // Kundendaten, steht der Stempel zwar in der Datenbank — die eingefrorene
  // Kopie kennt ihn aber nicht, und der Abschluss bliebe gesperrt. Der Fahrer
  // stünde vor der Haustür und käme nicht weiter.
  //
  // Deshalb wird der Stop bei jedem Rendern frisch aus der Tour geholt. Die
  // Kopie dient nur noch dazu, WELCHER Stop gemeint ist.
  const { data: tour } = useTour(date);
  const frisch = tour?.stops.find((s) => s.id === stop.id) ?? stop;

  // Kundenpflege: offen, solange der Modus läuft UND dieser Kunde noch nie
  // bestätigt wurde. Einmal bestätigt bleibt bestätigt — auch nächste Woche.
  const { data: pflege } = useKundenpflege();
  const kunde = frisch.order.customer;
  const pflegeOffen = !!pflege?.aktiv && !!kunde && !kunde.data_confirmed_at;

  // ⚠ `frisch`, nicht `stop`: Sobald der Fahrer den Korb an der Haustür ändert,
  // ist die eingefrorene Kopie veraltet. Auf dem Knopf stünde dann der ALTE
  // Betrag — und genau der würde als "kassiert" gebucht.
  const total = frisch.order.total_cents;

  // Scheitert der Abschluss (Funkloch, Konflikt), MUSS der Fahrer das sehen.
  // Vorher schluckte mutateAsync den Fehler und das Sheet blieb einfach offen —
  // der Fahrer wäre weitergefahren im Glauben, es sei gebucht.
  const deliver = async () => {
    setError(null);
    try {
      await complete.mutateAsync({
        stopId: stop.id,
        outcome: "delivered",
        paymentMethod: method,
        cashCents: method === "cash" ? total : undefined,
        signatureDataUrl: signature ?? undefined,
      });
      onClose();
    } catch (e) {
      setError(fahrerFehler(e, "Abschluss fehlgeschlagen — bitte erneut versuchen."));
    }
  };

  const markAbsent = async () => {
    setError(null);
    try {
      await complete.mutateAsync({ stopId: stop.id, outcome: "absent", reason });
      onClose();
    } catch (e) {
      setError(fahrerFehler(e, "Konnte nicht gespeichert werden."));
    }
  };

  if (absentMode) {
    return (
      <Sheet
        open
        onClose={onClose}
        title="Nicht erreicht"
        footer={
          <div className="space-y-2">
            {error && (
              <p className="rounded-lg border border-danger-bg/30 bg-danger-bg/10 px-3 py-2 text-sm text-danger-fg">
                {error}
              </p>
            )}
            <div className="flex gap-2">
              <Button size="lg" className="flex-1" onClick={() => setAbsentMode(false)}>
                Zurück
              </Button>
              <Button
                variant="danger"
                size="lg"
                className="flex-1"
                loading={complete.isPending}
                onClick={markAbsent}
              >
                Bestätigen
              </Button>
            </div>
          </div>
        }
      >
        <p className="mb-4 text-[15px] text-ink-300">
          Die Ware bleibt im Auto und landet am Tourende auf der Rückladeliste.
          Es wird keine Rechnung erzeugt.
        </p>
        <div className="flex flex-wrap gap-2">
          {["nicht angetroffen", "kein Geld da", "wollte nichts", "Zugang versperrt"].map((r) => (
            <Chip key={r} active={reason === r} onClick={() => setReason(r)}>
              {r}
            </Chip>
          ))}
        </div>
      </Sheet>
    );
  }

  return (
    <Sheet
      open
      onClose={onClose}
      title={stop.order.customer.display_name}
      footer={
        <div className="space-y-2">
          {error && (
            <p className="rounded-lg border border-danger-bg/30 bg-danger-bg/10 px-3 py-2 text-sm text-danger-fg">
              {error}
            </p>
          )}
          {/* Erst die Kundendaten, dann das Geld.
              Nach dem Kassieren ist der Kunde weg und der Fahrer im Auto — dann
              lässt sich nichts mehr fragen. Deshalb sperrt der Abschluss, solange
              der Kunde im Kundenpflege-Modus noch nicht bestätigt ist. */}
          {pflegeOffen ? (
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              onClick={() => setPflegeOffenSheet(true)}
            >
              Kundendaten prüfen — dann kassieren
            </Button>
          ) : (
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              loading={complete.isPending}
              onClick={deliver}
            >
              {method === "cash" ? `${euro(total)} kassiert — abschließen` : "Auf Rechnung — abschließen"}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="w-full"
            onClick={() => setAbsentMode(true)}
          >
            Kunde nicht erreicht
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {pflegeOffen && (
          <div className="rounded-lg border border-gruen-500/40 bg-gruen-500/10 px-4 py-3">
            <p className="text-sm font-medium text-gruen-400">Kundendaten noch nicht geprüft</p>
            <p className="mt-1 text-sm text-ink-300">
              Einmalig: Anschrift, Telefon und Lieferhinweis mit dem Kunden durchgehen.
              Danach taucht das hier nicht mehr auf.
            </p>
          </div>
        )}

        <section>
          <div className="mb-1 flex items-center justify-between">
            <SectionLabel>Ware</SectionLabel>
            {/* An der Haustür entscheidet der Kunde. Der Knopf steht bewusst
                NEBEN der Ware und nicht hinter einem Menü: Er wird bei jedem
                zweiten Stop gebraucht. */}
            <button
              onClick={() => setKorbOffen(true)}
              className="rounded-lg px-2 py-1 text-sm font-medium text-gruen-400 transition-colors duration-fast hover:bg-gruen-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gruen-500/60"
            >
              Korb ändern
            </button>
          </div>
          <ItemLines stop={frisch} />
          <div className="mt-3 flex items-baseline justify-between border-t border-ink-700/70 pt-3">
            <span className="text-sm text-ink-400">Summe</span>
            <span className="text-2xl font-semibold tabular-nums text-ink-50">{euro(total)}</span>
          </div>
        </section>

        <section>
          <div className="mb-1 flex items-center justify-between">
            <SectionLabel>Turnus</SectionLabel>
            {/* Rhythmus + Startpunkt an der Haustür setzen — Urlaubsrückkehr,
                neuer Kunde. „Ab heute zählt der Turnus neu." */}
            <button
              onClick={() => setTurnusOffen(true)}
              className="rounded-lg px-2 py-1 text-sm font-medium text-gruen-400 transition-colors duration-fast hover:bg-gruen-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gruen-500/60"
            >
              ändern
            </button>
          </div>
          <p className="text-sm text-ink-500">Wie oft dieser Kunde beliefert wird.</p>
        </section>

        <section>
          <SectionLabel>Zahlung</SectionLabel>
          <Segmented
            size="lg"
            value={method}
            onChange={setMethod}
            options={[
              { value: "cash", label: "Bar" },
              { value: "invoice", label: "Auf Rechnung" },
            ]}
          />
          <p className="mt-2 text-sm text-ink-500">
            {method === "cash"
              ? "Rechnung gilt sofort als bezahlt."
              : "Rechnung bleibt offen und wird gegen den Bankeingang abgeglichen."}
          </p>
        </section>

        <section>
          <SectionLabel>Unterschrift Kunde</SectionLabel>
          <SignaturePad onChange={setSignature} />
        </section>
      </div>

      {pflegeOffenSheet && kunde && (
        <KundenpflegeSheet
          kunde={kunde}
          adresse={frisch.order.address}
          onClose={() => setPflegeOffenSheet(false)}
        />
      )}

      {korbOffen && (
        <KorbSheet date={date} stop={frisch} onClose={() => setKorbOffen(false)} />
      )}

      {turnusOffen && kunde && (
        <TurnusSheet
          customerId={kunde.id}
          customerName={kunde.display_name}
          onClose={() => setTurnusOffen(false)}
        />
      )}
    </Sheet>
  );
}

// ─────────────────────────────────────────────── Korb an der Haustür ändern

/**
 * Was nimmt der Kunde heute wirklich?
 *
 * Zwei Listen in einem Blatt: oben, was im Korb liegt (mit Mengenwähler), unten
 * der Katalog zum Dazunehmen. Kein Suchfeld — es sind 14 Produkte, und wer mit
 * einer Eierpalette in der Hand tippt, will nicht erst eine Tastatur aufmachen.
 *
 * Gespeichert wird EINMAL, beim Übernehmen. Jede Mengenänderung sofort zu
 * schicken, hiesse bei "drei mal Plus antippen" drei Anfragen an der Bordsteinkante —
 * und im Funkloch drei halbe Ergebnisse.
 */
function KorbSheet({
  date,
  stop,
  onClose,
}: {
  date: string;
  stop: TourStop;
  onClose: () => void;
}) {
  const { data: produkte = [] } = useProducts({ onlyActive: true });
  const speichern = useStopKorb(date);
  const [fehler, setFehler] = useState<string | null>(null);

  // Entwurf: product_id → Menge. Startet beim aktuellen Stand des Stops.
  const [entwurf, setEntwurf] = useState<Record<string, number>>(() =>
    Object.fromEntries(stop.order.items.map((i) => [i.product_id, Number(i.qty)]))
  );

  const drin = produkte.filter((p) => (entwurf[p.id] ?? 0) > 0);
  const rest = produkte.filter((p) => !(entwurf[p.id] > 0));

  // Die Summe wird hier NUR angezeigt; verbindlich rechnet sie der Server neu.
  // Sie steht trotzdem da: Ohne sie tippt der Fahrer blind und erfährt den Preis
  // erst, wenn der Kunde schon das Portemonnaie zückt.
  const summe = drin.reduce((a, p) => a + (entwurf[p.id] ?? 0) * p.price_cents, 0);

  const uebernehmen = async () => {
    setFehler(null);
    try {
      await speichern.mutateAsync({
        stopId: stop.id,
        items: Object.entries(entwurf)
          .filter(([, qty]) => qty > 0)
          .map(([product_id, qty]) => ({ product_id, qty })),
      });
      onClose();
    } catch (e) {
      setFehler(fahrerFehler(e, "Der Korb konnte nicht gespeichert werden."));
    }
  };

  return (
    <Sheet
      open
      onClose={onClose}
      title="Korb anpassen"
      footer={
        <div className="space-y-2">
          {fehler && (
            <p className="rounded-lg border border-danger-bg/30 bg-danger-bg/10 px-3 py-2 text-sm text-danger-fg">
              {fehler}
            </p>
          )}
          <Button
            variant="primary"
            size="lg"
            className="w-full"
            loading={speichern.isPending}
            onClick={uebernehmen}
          >
            {euro(summe)} übernehmen
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Der Fahrer muss wissen, dass er hier die Dauerbestellung ändert und
            nicht nur den heutigen Stop — sonst „korrigiert" er einmalig etwas
            und wundert sich nächste Woche. */}
        <p className="rounded-lg border border-ink-700/70 bg-ink-800/40 px-3 py-2 text-sm text-ink-300">
          Was du hier änderst, gilt auch für die nächsten Lieferungen.
        </p>

        <section>
          <SectionLabel>Im Korb</SectionLabel>
          {drin.length === 0 ? (
            <p className="py-3 text-sm text-ink-500">
              Nichts drin — unten etwas antippen. Ein leerer Korb ist erlaubt:
              Dann wird nichts berechnet.
            </p>
          ) : (
            <ul className="space-y-2">
              {drin.map((p) => (
                <li
                  key={p.id}
                  className="flex items-center gap-3 rounded-xl border border-ink-700/70 bg-ink-900 px-3.5 py-2.5"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[15px] text-ink-100">{p.name}</span>
                    <span className="block text-sm text-ink-400">
                      {euro(p.price_cents)} · {euro((entwurf[p.id] ?? 0) * p.price_cents)}
                    </span>
                  </span>
                  {/* Schrittweite 1: Eier zählt man in Stück, Kartoffeln in
                      Säcken — jedes Gebinde ist ein eigener Artikel
                      (products.package_kg). Krumme Mengen kann der Fahrer
                      an der Haustür ohnehin nicht abwiegen. */}
                  <Stepper
                    value={entwurf[p.id] ?? 0}
                    onChange={(v) => setEntwurf((e) => ({ ...e, [p.id]: v }))}
                  />
                </li>
              ))}
            </ul>
          )}
        </section>

        {rest.length > 0 && (
          <section>
            <SectionLabel>Dazunehmen</SectionLabel>
            <div className="flex flex-wrap gap-2">
              {rest.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setEntwurf((e) => ({ ...e, [p.id]: 1 }))}
                  className="flex h-11 items-center gap-2 rounded-xl border border-ink-700 bg-ink-800/60 px-3.5 text-[15px] text-ink-200 transition-colors duration-fast hover:border-gruen-500/40 hover:text-ink-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gruen-500/60"
                >
                  <span className="text-gruen-400">+</span>
                  <span className="truncate">{p.name}</span>
                  <span className="text-sm text-ink-400">{euro(p.price_cents)}</span>
                </button>
              ))}
            </div>
          </section>
        )}
      </div>
    </Sheet>
  );
}

// ─────────────────────────────────────────────── Kundendaten am Stop prüfen

/**
 * Das Fenster, das den Kundenstamm in zwei Wochen einmal komplett durchsieht.
 *
 * Bewusst KEINE Pflichtfelder außer der Anschrift: 58 Kunden haben nicht einmal
 * einen Namen, und die meisten werden auch keine E-Mail herausgeben. Ein Feld,
 * das man nicht leer lassen darf, wird mit "-" gefüllt — dann steht Müll drin
 * und niemand sieht mehr, was wirklich fehlt.
 */
function KundenpflegeSheet({
  kunde,
  adresse,
  onClose,
}: {
  kunde: NonNullable<NonNullable<TourStop["order"]>["customer"]>;
  adresse: NonNullable<TourStop["order"]>["address"];
  onClose: () => void;
}) {
  const bestaetigen = useKundeBestaetigen();
  const [daten, setDaten] = useState<KundenpflegeDaten>({
    display_name: kunde.display_name ?? "",
    phone: kunde.phone ?? "",
    email: kunde.email ?? "",
    type: kunde.type === "gewerblich" ? "gewerblich" : "privat",
    street: adresse?.street ?? "",
    zip: adresse?.zip ?? "",
    city: adresse?.city ?? "",
    delivery_notes: adresse?.delivery_notes ?? "",
  });
  const [fehler, setFehler] = useState<string | null>(null);

  const setzen = (feld: keyof KundenpflegeDaten, wert: string) =>
    setDaten((d) => ({ ...d, [feld]: wert }));

  const speichern = async () => {
    setFehler(null);
    try {
      await bestaetigen.mutateAsync({
        customerId: kunde.id,
        addressId: adresse?.id ?? null,
        daten,
      });
      onClose();
    } catch (e) {
      // Ohne diese Anzeige bliebe das Fenster einfach offen stehen, und der
      // Fahrer tippt ratlos ein zweites Mal auf Speichern.
      setFehler(fahrerFehler(e, "Speichern fehlgeschlagen — bitte erneut versuchen."));
    }
  };

  return (
    <Sheet
      open
      onClose={onClose}
      title="Kundendaten prüfen"
      footer={
        <div className="space-y-2">
          {fehler && (
            <p className="rounded-lg border border-danger-bg/30 bg-danger-bg/10 px-3 py-2 text-sm text-danger-fg">
              {fehler}
            </p>
          )}
          <Button
            variant="primary"
            size="lg"
            className="w-full"
            disabled={!daten.street.trim()}
            loading={bestaetigen.isPending}
            onClick={speichern}
          >
            Stimmt so — bestätigen
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-ink-400">
          Einmalig mit dem Kunden durchgehen. Was fehlt, jetzt nachtragen — danach
          ist dieser Kunde erledigt.
        </p>

        <Field label="Name" hint="Darf leer bleiben — viele Kunden haben nur eine Adresse">
          <Input
            value={daten.display_name}
            onChange={(e) => setzen("display_name", e.target.value)}
            placeholder="Familie Meier"
          />
        </Field>

        <Field label="Straße und Hausnummer">
          <Input value={daten.street} onChange={(e) => setzen("street", e.target.value)} />
        </Field>

        <div className="grid grid-cols-[7rem_1fr] gap-3">
          <Field label="PLZ">
            <Input
              value={daten.zip}
              onChange={(e) => setzen("zip", e.target.value)}
              inputMode="numeric"
              className="tabular-nums"
            />
          </Field>
          <Field label="Ort">
            <Input value={daten.city} onChange={(e) => setzen("city", e.target.value)} />
          </Field>
        </div>

        <Field label="Telefon">
          <Input
            value={daten.phone ?? ""}
            onChange={(e) => setzen("phone", e.target.value)}
            inputMode="tel"
            placeholder="05241 …"
          />
        </Field>

        <Field label="E-Mail" hint="Für Rechnungen per Mail — sonst geht sie mit Papier raus">
          <Input
            value={daten.email ?? ""}
            onChange={(e) => setzen("email", e.target.value)}
            inputMode="email"
            autoCapitalize="off"
            placeholder="name@beispiel.de"
          />
        </Field>

        <Field label="Kundenart">
          <Segmented
            value={daten.type}
            onChange={(v) => setzen("type", v)}
            options={[
              { value: "privat", label: "Privat" },
              { value: "gewerblich", label: "Gewerblich" },
            ]}
          />
        </Field>

        <Field label="Lieferhinweis" hint="Wo abstellen, wer aufmacht, was zu beachten ist">
          <Input
            value={daten.delivery_notes ?? ""}
            onChange={(e) => setzen("delivery_notes", e.target.value)}
            placeholder="Stellt raus · kommt ans Auto · hinter den Blumenkübel"
          />
        </Field>
      </div>
    </Sheet>
  );
}

// ─────────────────────────────────────────────── Fehlertexte für den Fahrer

/**
 * Macht aus einem technischen Fehler einen Satz, mit dem Fabian an der Haustür
 * etwas anfangen kann.
 *
 * Ohne das steht dort wörtlich "Failed to fetch" — gemessen, nicht vermutet.
 * Das ist zwar besser als das Schweigen von vorher, sagt ihm aber nicht, was er
 * tun soll. Zwischen Versmold und Gütersloh ist Funkloch der Normalfall, kein
 * Ausnahmezustand: Die Meldung muss sagen, dass NICHTS gebucht wurde und dass es
 * mit Empfang wieder geht.
 */
export function fahrerFehler(e: unknown, fallback: string) {
  const roh = e instanceof Error ? e.message : String(e ?? "");
  const netzweg =
    !navigator.onLine ||
    /failed to fetch|networkerror|load failed|fetch/i.test(roh);
  if (netzweg) {
    return "Kein Empfang — es wurde nichts gebucht. Sobald wieder Netz da ist, nochmal tippen.";
  }
  return roh || fallback;
}

// ════════════════════════════════════════════════════════════ 5. ZURÜCK

function ReturnView({ date, auswahl }: { date: string; auswahl?: React.ReactNode }) {
  const { data: tour } = useTour(date);
  const { data: products = [] } = useProducts();
  const toggle = useToggleLoadItem(date);

  const items = (tour?.load_items ?? []).filter((i) => i.kind === "return");
  const done = items.filter((i) => i.checked_at).length;

  // ══ Eine leere Liste hat zwei Bedeutungen — sie dürfen nie verwechselt werden ══
  //
  // "Keine Rückladeposition" hieß hier bisher pauschal "das Auto ist leer".
  // Das ist nur dann wahr, wenn auch WIRKLICH jeder Stop beliefert wurde.
  // Scheitert dagegen der Aufbau der Liste, kommen ebenfalls null Positionen an —
  // und die App behauptete dann seelenruhig, der Wagen sei leer, während die
  // Ware von 200 Kunden darin steht.
  //
  // Die Wahrheit steht in den Stops, nicht in der Liste: Diese Ansicht erscheint
  // ausschließlich bei beendeter Tour (siehe oben). Ist die Tour beendet und ein
  // Stop NICHT beliefert, dann muss dessen Ware zurück — Punkt. Fehlt die Liste
  // trotzdem, ist etwas schiefgegangen, und das gehört gesagt.
  const offeneStops = (tour?.stops ?? []).filter((s) => s.status !== "delivered");

  if (!items.length) {
    if (offeneStops.length) {
      return (
        <div className="mx-auto flex h-full max-w-lg flex-col">
          <Header title="Tour beendet" subtitle={fmtDate(date)} />
          {auswahl}
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
            <KassenKarte tourId={tour?.id} />
            <EmptyState
              title="Rückladeliste fehlt"
              hint={`${offeneStops.length} ${offeneStops.length === 1 ? "Kunde wurde" : "Kunden wurden"} nicht beliefert — die Ware müsste also zurück ins Lager. Die Liste dazu konnte nicht erstellt werden. Bitte die Tour noch einmal beenden; hilft das nicht, im Büro melden. Auf keinen Fall ausladen, ohne nachzuzählen.`}
            />
          </div>
        </div>
      );
    }
    return (
      <div className="mx-auto flex h-full max-w-lg flex-col">
        <Header title="Tour beendet" subtitle={fmtDate(date)} />
        {auswahl}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          {/* Auch wenn nichts zurückgeht: Das Geld muss trotzdem abgegeben
              werden. Läge die Karte nur im Listen-Zweig, wäre sie an einem Tag
              ohne Restware unerreichbar — und die Kasse bliebe offen. */}
          <KassenKarte tourId={tour?.id} />
          <EmptyState
            title="Nichts zurückzuladen"
            hint="Alle Kunden wurden beliefert — das Auto ist leer."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex h-full max-w-lg flex-col">
      <Header title="Rückladeliste" subtitle={`${fmtDate(date)} · zurück ins Lager`} />
      {auswahl}

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        <KassenKarte tourId={tour?.id} />
        <p className="mb-4 text-sm text-ink-400">
          Diese Ware wurde nicht ausgeliefert und muss wieder eingelagert werden.
        </p>
        <ul className="space-y-2">
          {items.map((it) => {
            const p = products.find((x) => x.id === it.product_id);
            const checked = !!it.checked_at;
            return (
              <li key={it.id}>
                <button
                  onClick={() => toggle.mutate({ id: it.id, checked: !checked })}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-xl border px-4 py-4 text-left transition-colors duration-fast",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gruen-500/60",
                    checked
                      ? "border-gruen-500/30 bg-gruen-500/10"
                      : "border-ink-700 bg-ink-800/60"
                  )}
                >
                  <span
                    className={cn(
                      "flex h-7 w-7 shrink-0 items-center justify-center rounded-md border-2 text-sm font-bold",
                      checked ? "border-gruen-500 bg-gruen-500 text-ink-950" : "border-ink-600"
                    )}
                  >
                    {checked && "✓"}
                  </span>
                  <span className={cn("min-w-0 flex-1 text-[15px]", checked ? "text-ink-400 line-through" : "text-ink-100")}>
                    {p?.name ?? "?"}
                  </span>
                  <span className="shrink-0 text-lg font-semibold tabular-nums text-ink-50">
                    {qtyFmt(it.qty_total)}
                    <span className="ml-1 text-sm font-normal text-ink-400">
                      {p?.unit === "kg" ? "kg" : "St."}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      <Footer>
        <div className="flex items-center justify-between text-sm">
          <span className="text-ink-400">Eingelagert</span>
          <span className="tabular-nums text-ink-100">
            {done} / {items.length}
          </span>
        </div>
      </Footer>
    </div>
  );
}

// ══════════════════════════════════════════════════════ 6. KASSE ABGEBEN

/**
 * Das Bargeld dieser Tour — Stand und Weg zur Abgabe.
 *
 * Solange Geld offen ist, steht die Karte auffällig oben auf dem
 * Abschluss-Bildschirm: Am Dienstag sind das rund 1.800 €, und die sind
 * wichtiger als drei Säcke Kartoffeln. Ist abgegeben, schrumpft sie auf eine
 * ruhige Bestätigungszeile — erledigt ist erledigt.
 *
 * Ohne Bargeld (alles auf Rechnung) erscheint sie gar nicht.
 */
function KassenKarte({ tourId }: { tourId: string | undefined }) {
  const { data: offen, isLoading } = useOffeneKasse(tourId);
  const { data: abgabe } = useKassenAbgabe(tourId);
  const [sheetOffen, setSheetOffen] = useState(false);

  if (isLoading) return null;

  // Schon abgegeben → ruhige Quittung.
  if (abgabe) {
    const stimmt = abgabe.diff_cents === 0;
    return (
      <Card className="mb-4 p-4">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-gruen-500 text-sm font-bold text-gruen-on">
            ✓
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[15px] font-medium text-ink-100">
              Kasse abgegeben · {euro(abgabe.ist_cents)}
            </div>
            <div className="mt-0.5 text-sm tabular-nums text-ink-400">
              {kurzDatum(abgabe.handed_over_at)}
              {abgabe.witness?.full_name && ` · an ${abgabe.witness.full_name}`}
            </div>
            {!stimmt && (
              <div className="mt-1 text-sm font-medium text-warn-fg">
                Differenz {abgabe.diff_cents > 0 ? "+" : ""}
                {euro(abgabe.diff_cents)}
                {abgabe.note && <span className="font-normal text-ink-400"> · {abgabe.note}</span>}
              </div>
            )}
          </div>
        </div>
      </Card>
    );
  }

  // Kein Bargeld auf dieser Tour → nichts zu tun, nichts zu zeigen.
  if (!offen || offen.anzahl === 0) return null;

  return (
    <>
      <Card className="mb-4 border-gruen-500/40 bg-gruen-500/10 p-4">
        <div className="text-xs font-medium uppercase tracking-wider text-ink-400">
          Bargeld aus dieser Tour
        </div>
        <div className="mt-1 text-3xl font-semibold tabular-nums text-ink-50">
          {euro(offen.summe)}
        </div>
        <div className="mt-0.5 text-sm text-ink-400">
          von {offen.anzahl} {offen.anzahl === 1 ? "Kunden" : "Kunden"} kassiert
        </div>
        <Button
          variant="primary"
          size="lg"
          className="mt-3 w-full"
          onClick={() => setSheetOffen(true)}
        >
          Kasse abgeben
        </Button>
      </Card>

      {sheetOffen && (
        <KasseAbgebenSheet
          tourId={tourId!}
          soll={offen.summe}
          anzahl={offen.anzahl}
          onClose={() => setSheetOffen(false)}
        />
      )}
    </>
  );
}

/**
 * Zählen, vergleichen, übergeben.
 *
 * Der Sinn des Ganzen ist der ABGLEICH — deshalb wird gezählt und nicht nur
 * bestätigt. Wer nur bestätigt, unterschreibt eine Zahl, die die App sich selbst
 * ausgerechnet hat; ein Fehlbetrag fiele nie auf.
 *
 * Bei einer Abweichung wird die Notiz zur Pflicht: "−5,00 €" ohne Erklärung ist
 * wertlos, und drei Wochen später weiß es niemand mehr.
 */
function KasseAbgebenSheet({
  tourId,
  soll,
  anzahl,
  onClose,
}: {
  tourId: string;
  soll: number;
  anzahl: number;
  onClose: () => void;
}) {
  const abgeben = useKasseAbgeben(tourId);
  const { data: team = [] } = useTeam();
  const [istText, setIstText] = useState("");
  const [empfaenger, setEmpfaenger] = useState("");
  const [notiz, setNotiz] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);

  // Sebastian ist der übliche Empfänger — vorausgewählt, aber änderbar.
  useEffect(() => {
    if (empfaenger) return;
    const seb = team.find((m) => /sebastian/i.test(m.full_name ?? ""));
    if (seb) setEmpfaenger(seb.id);
  }, [team, empfaenger]);

  /** "12,50" und "12.50" sind dasselbe — am Handy tippt niemand gern das Komma. */
  const istCents = (() => {
    const s = istText.trim().replace(",", ".");
    if (!s) return null;
    const n = Number(s);
    if (!isFinite(n) || n < 0) return null;
    return Math.round(n * 100);
  })();

  const diff = istCents === null ? null : istCents - soll;
  const stimmt = diff === 0;
  const brauchtNotiz = diff !== null && diff !== 0;
  const kannAbgeben =
    istCents !== null && !!empfaenger && (!brauchtNotiz || notiz.trim().length > 2);

  async function los() {
    setFehler(null);
    try {
      await abgeben.mutateAsync({
        istCents: istCents!,
        witnessId: empfaenger || null,
        note: notiz.trim() || null,
      });
      onClose();
    } catch (e) {
      const m = (e as Error).message ?? "";
      setFehler(
        /failed to fetch|load failed|networkerror/i.test(m)
          ? "Kein Netz — die Kasse wurde NICHT abgegeben. Bitte noch einmal tippen."
          : /duplicate|unique/i.test(m)
            ? "Diese Tour wurde bereits abgegeben."
            : m || "Die Abgabe hat nicht geklappt."
      );
    }
  }

  return (
    <Sheet
      open
      onClose={onClose}
      title="Kasse abgeben"
      footer={
        <div className="space-y-2">
          {fehler && (
            <p className="rounded-lg border border-danger-bg/40 bg-danger-bg/10 px-3 py-2.5 text-sm text-danger-fg">
              {fehler}
            </p>
          )}
          <Button
            variant="primary"
            size="lg"
            className="w-full"
            disabled={!kannAbgeben}
            loading={abgeben.isPending}
            onClick={los}
          >
            {istCents === null ? "Betrag eingeben" : `${euro(istCents)} abgeben`}
          </Button>
          {brauchtNotiz && notiz.trim().length <= 2 && (
            <p className="text-center text-xs text-ink-500">
              Bei einer Abweichung braucht es eine kurze Erklärung.
            </p>
          )}
        </div>
      }
    >
      <div className="space-y-4">
        {/* Soll — was die App ausgerechnet hat */}
        <div className="rounded-xl border border-ink-700 bg-ink-800/60 p-4">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-sm text-ink-400">Kassiert (Soll)</span>
            <span className="text-2xl font-semibold tabular-nums text-ink-50">{euro(soll)}</span>
          </div>
          <div className="mt-0.5 text-sm text-ink-500">
            aus {anzahl} {anzahl === 1 ? "Barzahlung" : "Barzahlungen"}
          </div>
        </div>

        {/* Ist — was wirklich da ist */}
        <Field label="Gezählt (Ist)">
          <Input
            type="text"
            inputMode="decimal"
            value={istText}
            onChange={(e) => setIstText(e.target.value)}
            placeholder="z.B. 1810,68"
            className="text-xl tabular-nums"
            autoFocus
          />
        </Field>

        {/* Differenz — der eigentliche Zweck des Vorgangs */}
        {diff !== null && (
          <div
            className={cn(
              "rounded-xl border px-4 py-3",
              stimmt
                ? "border-gruen-500/40 bg-gruen-500/10"
                : "border-warn-bg/50 bg-warn-bg/10"
            )}
          >
            <div className="flex items-baseline justify-between gap-3">
              <span className={cn("text-sm", stimmt ? "text-gruen-400" : "text-warn-fg")}>
                {stimmt ? "Stimmt genau" : diff > 0 ? "Zu viel" : "Es fehlt"}
              </span>
              <span
                className={cn(
                  "text-xl font-semibold tabular-nums",
                  stimmt ? "text-gruen-400" : "text-warn-fg"
                )}
              >
                {diff > 0 ? "+" : ""}
                {euro(diff)}
              </span>
            </div>
          </div>
        )}

        <Field label="Entgegengenommen von">
          <select
            value={empfaenger}
            onChange={(e) => setEmpfaenger(e.target.value)}
            className={cn(
              "h-11 w-full rounded-lg border bg-ink-900 px-3 text-base focus:outline-none focus:ring-2 focus:ring-gruen-500/20",
              empfaenger
                ? "border-ink-700 text-ink-100 focus:border-gruen-500/60"
                : "border-warn-bg/50 text-ink-400"
            )}
          >
            <option value="">wer nimmt es entgegen?</option>
            {team.map((m) => (
              <option key={m.id} value={m.id}>
                {m.full_name ?? "ohne Namen"}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Notiz" optional={!brauchtNotiz}>
          <Input
            value={notiz}
            onChange={(e) => setNotiz(e.target.value)}
            placeholder={brauchtNotiz ? "Woher kommt die Abweichung?" : "nur wenn nötig"}
          />
        </Field>
      </div>
    </Sheet>
  );
}

// ─────────────────────────────────────────────────────── Bausteine

/**
 * Zahlungsstand des Kunden, direkt auf der Stop-Karte.
 *
 * Der Fahrer soll NICHT erst in einer Akte blättern müssen. Steht nichts offen,
 * bleibt die Zeile unaufdringlich grün. Steht etwas offen, springt sie ins Auge —
 * dann kann er das Geld gleich mitkassieren, statt eine Mahnung loszutreten.
 */
function Zahlungshinweis({ customerId }: { customerId: string }) {
  const { data: rechnungen = [], isLoading } = useKundenRechnungen(customerId);
  if (isLoading) return null;

  const stand = zahlungsstand(rechnungen);
  if (!stand.letzte) return null; // Erstlieferung — es gibt nichts zu berichten

  const offen = stand.offeneAnzahl > 0;

  return (
    <div
      className={cn(
        "mb-4 flex items-center justify-between gap-3 rounded-xl border px-4 py-3",
        offen
          ? "border-danger-bg/40 bg-danger-bg/10"
          : "border-gruen-500/30 bg-gruen-500/10"
      )}
    >
      <div className="min-w-0">
        <div
          className={cn(
            "text-[15px] font-medium",
            offen ? "text-danger-fg" : "text-gruen-400"
          )}
        >
          {offen
            ? `${stand.offeneAnzahl} ${stand.offeneAnzahl === 1 ? "Rechnung" : "Rechnungen"} offen`
            : "Alles bezahlt"}
        </div>
        <div className="mt-0.5 text-sm text-ink-400">
          Letzte Lieferung {kurzDatum(stand.letzte.datum)}:{" "}
          {stand.letzte.bezahlt ? "bezahlt" : "nicht bezahlt"}
        </div>
      </div>

      {offen && (
        <div className="shrink-0 text-lg font-semibold tabular-nums text-danger-fg">
          {euro(stand.offenCents)}
        </div>
      )}
    </div>
  );
}

function kurzDatum(iso: string) {
  return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
}

function ItemLines({ stop }: { stop: TourStop }) {
  const { data: products = [] } = useProducts();
  return (
    <ul className="space-y-1.5 text-[15px]">
      {stop.order.items.map((it) => {
        const p = products.find((x) => x.id === it.product_id);
        const menge = Number(it.qty);

        // An der Haustür zählt, WELCHEN Sack man greift — nicht wie viele Kilo
        // das sind. "2× Sack 5 kg" ist eine Anweisung, "10 kg Kartoffeln" ist
        // eine Rechenaufgabe.
        const text = p?.package_kg
          ? `${qtyFmt(menge)}× Sack ${qtyFmt(Number(p.package_kg))} kg — ${p.name.replace(/\s*\d+(,\d+)?\s*kg$/, "")}`
          : p?.unit === "kg"
            ? `${qtyFmt(menge)} kg ${p?.name}`
            : `${qtyFmt(menge)}× ${p?.name ?? "?"}`;

        return (
          <li key={it.id} className="flex items-baseline justify-between gap-3">
            <span className="min-w-0 truncate text-ink-200">{text}</span>
            <span className="shrink-0 tabular-nums text-ink-400">
              {euro(Math.round(menge * it.unit_price_cents))}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/** Eigene Kopfzeile gab es hier mal — jetzt dieselbe wie überall, inklusive der
 *  drei Striche links. Ohne sie käme der Fahrer aus der Tour nur über den
 *  Startbildschirm heraus. */
function Header({ title, subtitle }: { title: string; subtitle?: string }) {
  return <ModulHeader titel={title} untertitel={subtitle} />;
}

function Footer({ children }: { children: React.ReactNode }) {
  return (
    <footer className="border-t border-ink-700/70 bg-ink-900/95 px-4 py-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
      {children}
    </footer>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card className="px-4 py-3">
      <div className="text-xs uppercase tracking-wider text-ink-400">{label}</div>
      <div className="mt-1 text-xl font-semibold tabular-nums text-ink-50">{value}</div>
    </Card>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="mb-2.5 text-xs font-medium uppercase tracking-wider text-ink-400">
      {children}
    </h3>
  );
}

function fmtDate(iso: string) {
  return new Date(`${iso}T00:00:00`).toLocaleDateString("de-DE", {
    weekday: "long",
    day: "2-digit",
    month: "2-digit",
  });
}
